/**
 * What a file or folder row says on a phone: a glyph and tint for the file's
 * type, its name, and a subtitle of document number, revision and status,
 * or size and date when document control has nothing to say. Shared by the
 * web Documents list and the native app.
 */
import { format } from 'date-fns';
import {
  d_audio_file,
  d_code,
  d_description,
  d_draft,
  d_folder_zip,
  d_image,
  d_table_view,
  d_video_file,
} from '../../shared/ui/iconPaths';
import { FOLDER_MIME_TYPE } from '../../features/documents/documentConstants';
import type { GoogleDriveFile } from '../../types/documents';

export const formatFileSize = (bytes: number) => {
  if (!bytes || Number.isNaN(bytes)) return 'N/A';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

export type DocumentFileKind = 'folder' | 'image' | 'audio' | 'video' | 'archive' | 'spreadsheet' | 'code' | 'pdf' | 'document' | 'file';

/** The web register's filled folder glyph, on its own grid. */
export const FOLDER_GLYPH = {
  d: 'M3.06641 17.8711L19.4043 17.8711C21.1523 17.8711 22.1777 16.8555 22.1777 14.8438L22.1777 4.81445C22.1777 2.80273 21.1426 1.78711 19.1113 1.78711L9.24805 1.78711C8.58398 1.78711 8.18359 1.63086 7.69531 1.21094L7.09961 0.722656C6.45508 0.175781 5.95703 0 4.99023 0L2.69531 0C0.986328 0 0 0.976562 0 2.93945L0 14.8438C0 16.8652 1.02539 17.8711 3.06641 17.8711ZM1.80664 5.39062L1.80664 4.84375C1.80664 4.24805 2.22656 3.86719 2.95898 3.86719L19.1797 3.86719C19.9023 3.86719 20.332 4.24805 20.332 4.84375L20.332 5.39062Z',
  viewBox: '0 0 22.5391 17.998',
};

/** The Apple system tints the web list has always used per file type. No
 *  tint means the row's muted colour: folders stay neutral so file-type
 *  colours stand out. */
const KIND_STYLE: Record<DocumentFileKind, { iconPath: string; viewBox?: string; symbol?: string; tint?: string }> = {
  // The folder path is traced from this symbol, so native draws the symbol itself.
  folder: { iconPath: FOLDER_GLYPH.d, viewBox: FOLDER_GLYPH.viewBox, symbol: 'folder.fill' },
  image: { iconPath: d_image, tint: '#34C759' },
  audio: { iconPath: d_audio_file, tint: '#FF9500' },
  video: { iconPath: d_video_file, tint: '#FF375F' },
  archive: { iconPath: d_folder_zip, tint: '#A2845E' },
  spreadsheet: { iconPath: d_table_view, tint: '#34C759' },
  code: { iconPath: d_code, tint: '#5AC8FA' },
  pdf: { iconPath: d_description, tint: '#FF3B30' },
  document: { iconPath: d_description, tint: '#3B82F6' },
  file: { iconPath: d_draft },
};

export function documentFileKind(mimeType?: string | null): DocumentFileKind {
  const m = (mimeType || '').toLowerCase();
  if (m === FOLDER_MIME_TYPE) return 'folder';
  if (m.includes('image')) return 'image';
  if (m.includes('audio')) return 'audio';
  if (m.includes('video')) return 'video';
  if (['zip', 'rar', '7z', 'tar', 'compressed'].some((t) => m.includes(t))) return 'archive';
  if (['spreadsheet', 'excel', 'sheet', 'csv', 'tsv'].some((t) => m.includes(t))) return 'spreadsheet';
  if (['json', 'xml', 'javascript', 'html'].some((t) => m.includes(t))) return 'code';
  if (m.includes('pdf')) return 'pdf';
  if (['document', 'text', 'word'].some((t) => m.includes(t))) return 'document';
  return 'file';
}

/** The type word the web uses beside a file's size: "PDF", "Excel", or the extension. */
export const getFileType = (name: string, mimeType: string) => {
  const type = (mimeType || '').toLowerCase();
  if (type === FOLDER_MIME_TYPE) return 'Folder';
  if (type === 'application/vnd.google-apps.document') return 'Google Doc';
  if (type === 'application/vnd.google-apps.spreadsheet') return 'Google Sheet';
  if (type === 'application/vnd.google-apps.presentation') return 'Google Slide';
  if (type === 'application/vnd.google-apps.drawing') return 'Google Drawing';
  if (type === 'application/vnd.google-apps.form') return 'Google Form';
  if (type === 'application/pdf') return 'PDF';
  if (type === 'text/csv') return 'CSV';
  if (type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'Word';
  if (type === 'application/msword') return 'Word';
  if (type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'Excel';
  if (type === 'application/vnd.ms-excel') return 'Excel';
  if (type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return 'PowerPoint';
  if (type === 'application/vnd.ms-powerpoint') return 'PowerPoint';
  if (type.startsWith('image/')) return 'Image';
  if (type.startsWith('video/')) return 'Video';
  if (type.startsWith('audio/')) return 'Audio';
  if (type.includes('spreadsheet')) return 'Spreadsheet';
  if (type.includes('presentation')) return 'Presentation';
  if (type.includes('document')) return 'Document';
  const extension = name.split('.').pop();
  return extension ? extension.toUpperCase() : 'File';
};

export const documentKindStyle = (kind: DocumentFileKind) => KIND_STYLE[kind];

/** The phone row's second line. */
export function documentRowSubtitle(doc: Pick<GoogleDriveFile, 'mimeType' | 'modifiedTime' | 'size' | 'documentControl'>): string {
  const isFolder = doc.mimeType === FOLDER_MIME_TYPE;
  let updated = '-';
  try { if (doc.modifiedTime) updated = format(new Date(doc.modifiedTime), 'dd-MM-yyyy'); } catch { updated = '-'; }
  if (isFolder) return doc.modifiedTime ? `Updated ${updated}` : 'Folder';
  const control = doc.documentControl;
  const revision = control?.revisionCode || control?.currentRevisionCode;
  const status = control?.revisionPending ? 'Pending revision' : control?.statusCode || control?.reviewState;
  // Native storage sends size as a string.
  const bytes = Number(doc.size);
  return [control?.documentNumber, revision && `Rev ${revision}`, status].filter(Boolean).join(' · ')
    || [bytes ? formatFileSize(bytes) : null, doc.modifiedTime ? updated : null].filter(Boolean).join(' · ');
}

/** The desktop list's cells, worded as the web register words them. Folders leave the document cells empty. */
export interface DocumentRowColumns {
  documentNumber: string;
  revision: string;
  status: string;
  discipline: string;
  updated: string;
  size: string;
}

export interface DocumentRowModel {
  id: string;
  name: string;
  isFolder: boolean;
  subtitle: string;
  columns: DocumentRowColumns;
  iconPath: string;
  iconViewBox?: string;
  iconSymbol?: string;
  tint?: string;
}

export function documentRowColumns(doc: Pick<GoogleDriveFile, 'mimeType' | 'modifiedTime' | 'size' | 'documentControl'>): DocumentRowColumns {
  const isFolder = doc.mimeType === FOLDER_MIME_TYPE;
  const control = doc.documentControl;
  let updated = '-';
  try { if (doc.modifiedTime) updated = format(new Date(doc.modifiedTime), 'd MMM yy'); } catch { updated = '-'; }
  // Native storage sends size as a string.
  const bytes = Number(doc.size);
  if (isFolder) return { documentNumber: '', revision: '', status: '', discipline: '', updated, size: '' };
  return {
    documentNumber: control?.documentNumber || '',
    revision: control?.revisionCode || control?.currentRevisionCode || '-',
    status: (control?.revisionPending ? 'Pending revision' : control?.statusCode || control?.reviewState) || '-',
    discipline: control?.discipline || '-',
    updated,
    size: bytes ? formatFileSize(bytes) : '',
  };
}

export function buildDocumentRowModel(doc: GoogleDriveFile): DocumentRowModel {
  const kind = documentFileKind(doc.mimeType);
  const style = KIND_STYLE[kind];
  return { id: doc.id, name: doc.name, isFolder: kind === 'folder', subtitle: documentRowSubtitle(doc), columns: documentRowColumns(doc), iconPath: style.iconPath, iconViewBox: style.viewBox, iconSymbol: style.symbol, tint: style.tint };
}
